import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Chip,
  TextField,
  MenuItem,
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  Science,
  Numbers,
  LocationOn,
  CalendarMonth,
  Category,
  Badge,
  Flag,
  Person,
  Place,
  Description,
  Comment as CommentIcon,
} from "@mui/icons-material";
import {
  PRIORITY_OPTIONS,
  LAB_OPTIONS,
  withCurrentValue,
} from "./sampleFieldOptions";
import { EMPTY_LOOKUPS } from "./useSampleLookups";
import QuickAddValueDialog from "./QuickAddValueDialog";

const toDateInputValue = (date) => (date ? date.split("T")[0] : "");
// Use the date/time exactly as the API sends it, cut to "YYYY-MM-DDTHH:mm".
const toDateTimeInputValue = (date) => (date ? date.slice(0, 16) : "");

// samples.OtherLabName stores a certification id; show the lab's everyday name
// where we know it so the panel reads the way the Edit Sample dropdown does.
const formatLab = (value) => {
  if (!value) return "";
  const match = LAB_OPTIONS.find((option) => option.value === value);
  return match ? match.label : value;
};

// Renders one field as a small always-editable control (click in, edit, tab or
// pick to save) instead of read-only text - mirrors the quick-edit feel of the
// ordered bottle expanded view and of the legacy VLP grid.
const EditableField = ({
  recordid,
  field,
  initialValue,
  type,
  options,
  onFieldSave,
}) => {
  const [value, setValue] = useState(initialValue ?? "");
  const [saving, setSaving] = useState(false);

  // Re-sync when a different sample is shown or the underlying data changes
  // elsewhere.
  useEffect(() => {
    setValue(initialValue ?? "");
  }, [recordid, initialValue]);

  const commit = async (next) => {
    const previous = initialValue ?? (type === "checkbox" ? false : "");
    if (next === previous) return;

    setSaving(true);
    try {
      await onFieldSave(field, next);
    } catch (error) {
      setValue(previous);
    } finally {
      setSaving(false);
    }
  };

  if (type === "checkbox") {
    return (
      <Checkbox
        size="small"
        sx={{ p: 0.25 }}
        checked={!!value}
        disabled={saving}
        onChange={(e) => {
          setValue(e.target.checked);
          commit(e.target.checked);
        }}
      />
    );
  }

  if (options) {
    return (
      <TextField
        select
        variant="standard"
        size="small"
        fullWidth
        value={value}
        disabled={saving}
        onChange={(e) => {
          setValue(e.target.value);
          commit(e.target.value);
        }}
        InputProps={{
          sx: { fontSize: "0.8rem" },
          endAdornment: saving ? <CircularProgress size={12} /> : null,
        }}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {withCurrentValue(options, value).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      type={type === "date" || type === "datetime-local" ? type : "text"}
      value={value}
      disabled={saving}
      onChange={(e) => setValue(e.target.value)}
      // An emptied date has to go back as null, not "", or the API rejects it.
      onBlur={() =>
        commit(
          (type === "date" || type === "datetime-local") && value === ""
            ? null
            : value
        )
      }
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      InputProps={{
        sx: { fontSize: "0.8rem" },
        endAdornment: saving ? <CircularProgress size={12} /> : null,
      }}
    />
  );
};

// Compact panel shown when a sample row is expanded, laid out the same way as
// the ordered bottle expanded view. Fields carrying a `field` key save through
// the update-sample endpoint on blur; the rest are display only.
const ViewSample = ({ sampleData, onFieldSave, lookups = EMPTY_LOOKUPS }) => {
  // Which client-scoped list the "+" is currently adding to, if any.
  const [quickAdd, setQuickAdd] = useState(null);

  const quickAddConfig = {
    facilityId: {
      title: "Add Facility ID",
      label: "Facility ID",
      onAdd: lookups.addFacilityId,
    },
    samplingPointId: {
      title: "Add Sampling Point ID",
      label: "Sampling Point ID",
      onAdd: lookups.addSamplingPointId,
    },
  };

  // Adding from here also puts the new value on this sample, which is the
  // point of the shortcut. Saving through onFieldSave rather than into the
  // input's own state keeps the table the single source of truth.
  const handleQuickAdd = async (name) => {
    const added = await quickAddConfig[quickAdd].onAdd(name);
    if (added) {
      await onFieldSave(sampleData, quickAdd, added);
    }
  };

  if (!sampleData) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No sample data available
        </Typography>
      </Box>
    );
  }

  // Data-driven fields so every cell renders through the same markup and the
  // labels/values line up in a real grid. The table already shows order
  // number, sample number, lab number, dates, matrix, lab id, rush and hold,
  // so this panel carries the detail that is not on the row.
  //
  // Client Job Number and Global Job Number have no `field` key: the
  // update-sample endpoint does not write those columns, so they stay display
  // only rather than looking editable and silently discarding the change.
  const fields = [
    {
      label: "Sample Location",
      field: "sampleIdentity",
      value: sampleData.sampleIdentity,
      icon: LocationOn,
    },
    {
      label: "Sample Description",
      field: "sampleDescription",
      value: sampleData.sampleDescription,
      icon: Description,
    },
    {
      label: "Sample Type",
      field: "samtype",
      value: sampleData.samtype,
      options: lookups.sampleTypes,
      icon: Category,
    },
    {
      label: "Priority",
      field: "priority",
      value: sampleData.priority,
      options: PRIORITY_OPTIONS.map((value) => ({ value, label: value })),
      icon: Flag,
    },
    {
      label: "Process ID",
      field: "processId",
      value: sampleData.processId,
      icon: Numbers,
    },
    {
      label: "Facility ID",
      field: "facilityId",
      value: sampleData.facilityId,
      options: lookups.facilityIds,
      quickAddKey: "facilityId",
      icon: Badge,
    },
    {
      label: "Sampling Point ID",
      field: "samplingPointId",
      value: sampleData.samplingPointId,
      options: lookups.samplingPointIds,
      quickAddKey: "samplingPointId",
      icon: Place,
    },
    {
      label: "Lab",
      field: "otherLabName",
      value: sampleData.otherLabName,
      display: formatLab(sampleData.otherLabName),
      options: LAB_OPTIONS,
      icon: Science,
    },
    { label: "County", field: "county", value: sampleData.county, icon: Place },
    {
      label: "Collected By",
      field: "collectedBy",
      value: sampleData.collectedBy,
      icon: Person,
    },
    {
      // VLP stores the received time inside date_received, so the date and
      // time are edited together here; the API derives time_received from it.
      label: "Date/Time Received",
      field: "dateReceived",
      value: toDateTimeInputValue(sampleData.dateReceived),
      type: "datetime-local",
      icon: CalendarMonth,
    },
    {
      label: "Original Sample Date",
      field: "originalSampleDate",
      value: toDateInputValue(sampleData.originalSampleDate),
      type: "date",
      icon: CalendarMonth,
    },
    {
      label: "Compliance Sample",
      field: "complianceSample",
      value: !!sampleData.complianceSample,
      display: sampleData.complianceSample ? "Yes" : "No",
      type: "checkbox",
      icon: Flag,
    },
    { label: "Client Job Number", value: sampleData.clientJobNumber, icon: Badge },
    { label: "Global Job Number", value: sampleData.globalJobNumber, icon: Badge },
    {
      label: "Comments",
      field: "comments",
      value: sampleData.comments,
      icon: CommentIcon,
      wide: true,
    },
  ];

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "background.default",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      {/* Compact header - one line */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Science sx={{ color: "primary.main", fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Sample
        </Typography>
        <Chip
          label={`#${sampleData.sampleNumber ?? "N/A"}`}
          color="primary"
          size="small"
          sx={{ height: 18, fontSize: "0.7rem", fontWeight: 600 }}
        />
        {sampleData.labId && (
          <Typography variant="caption" color="text.secondary">
            Lab ID: {sampleData.labId}
          </Typography>
        )}
      </Box>

      {/* Fixed-width columns via CSS grid keep labels/values aligned
          regardless of how long any individual value is.

          The column count is pinned per breakpoint rather than left to
          auto-fill: opening a dropdown locks page scroll, which moves the
          container width by a scrollbar, and an auto-fill track count would
          re-wrap the fields onto different rows while the menu is open. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            lg: "repeat(6, minmax(0, 1fr))",
          },
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {fields.map(
          ({
            label,
            field,
            value,
            display,
            options,
            quickAddKey,
            icon: Icon,
            wide,
            type,
          }) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                gridColumn: wide ? "1 / -1" : undefined,
              }}
            >
              {Icon && (
                <Icon
                  sx={{
                    color: "primary.main",
                    fontSize: 14,
                    mt: "2px",
                    opacity: 0.6,
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.2, fontSize: "0.7rem" }}
                >
                  {label}
                </Typography>
                {onFieldSave && field ? (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EditableField
                        recordid={sampleData.recordid}
                        field={field}
                        initialValue={value}
                        type={type}
                        options={options}
                        onFieldSave={(f, v) => onFieldSave(sampleData, f, v)}
                      />
                    </Box>
                    {/* The "+" VLP puts next to Facility Id and Sampling
                        Point: add a value to the client's list on the spot. */}
                    {quickAddKey && (
                      <Tooltip title={`Add ${label}`}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            sx={{ p: 0.25 }}
                            onClick={() => setQuickAdd(quickAddKey)}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      wordBreak: "break-word",
                      fontSize: "0.8rem",
                      lineHeight: 1.3,
                    }}
                  >
                    {display || value || "N/A"}
                  </Typography>
                )}
              </Box>
            </Box>
          )
        )}
      </Box>

      <QuickAddValueDialog
        open={quickAdd !== null}
        title={quickAdd ? quickAddConfig[quickAdd].title : ""}
        label={quickAdd ? quickAddConfig[quickAdd].label : ""}
        onClose={() => setQuickAdd(null)}
        onAdd={handleQuickAdd}
      />
    </Box>
  );
};

export default ViewSample;
